import React, {useState, useEffect}					from 'react';
import {useNavigate, useParams}						from 'react-router-dom';
import moment										from 'moment';

import UsersImport									from './UserImport';
import UsersDocuments 								from './UserDocuments';
import UsersModalAssignDevice 						from './UserAssignDevice';
import Button										from '../ressources/components/Button';
import Input										from '../ressources/components/Input';
import MultiInputContainer							from '../ressources/components/Input/MultiInputContainer';
import Tabs											from '../ressources/components/Tabs';
import ProfilePicture								from '../ressources/components/Avatar/ProfilePicture';
import ButtonStickyContainer						from '../ressources/components/Button/ButtonStickyContainer';
import Select										from '../ressources/components/Select';
import ModalTags									from '../ressources/components/ModalTags';
import Badge										from '../ressources/components/Badge';
import Icon											from '../ressources/components/Icon';

import {useAppSelector, useAppDispatch}				from '../app/hooks';
import {postCompanyUsersApi,
	uploadProfilePictureApi}						from '../redux/companyUsers/companyUsers';
import {assignDevicesApi}							from '../redux/devices/devices';
import {PostUserData}								from '../redux/companyUsers/interface';
import {pushUpload, setUploadPercent}				from '../redux/documents/documents';
import {InputValues}								from '../ressources/components/Input/interface';
import {Tags}										from '../ressources/components/ModalTags/interface';

import {ft_genNotification, ft_pushNotification} 	from '../ressources/functions/notification';
import {ft_formatUserName}							from '../ressources/functions/format';
import {regex_email, regex_phone}								from '../ressources/data/regex';

import img_faceSmile								from '../ressources/images/icon/face-smile.svg';
import img_device									from '../ressources/images/icon/devices/computer.svg';
import img_upload									from '../ressources/images/icon/upload.svg';
import img_userPlus									from '../ressources/images/icon/user-plus-01.svg';

import LANGUAGE										from '../ressources/language';
import * as API										from '../ressources/API';

import './Users.css';


function UserAdd() {
	const dispatch = useAppDispatch();
	const user = useAppSelector(state => state.user.data);
	const lang = useAppSelector(state => state.settings.lang);
	const documentsUpload = useAppSelector(state => state.documents.uploadDocument);
	let notificationId = useAppSelector(state => state.notifications.lastId);

	const [view, set_view] = useState('home');
	const [step, set_step] = useState<number>(1);
	const [tagsList, set_tagsList] = useState<Tags[]>([]);
	const [modal, set_modal] = useState<'tags' | null>();

	const [profilePicture, set_profilePicture] = useState<File | undefined>(undefined);
	const [profilePictureImg, set_profilePictureImg] = useState<string | undefined>(undefined);
	const [firstname, set_firstname] = useState<InputValues>('');
	const [lastname, set_lastname] = useState<InputValues>('');
	const [job, set_job] = useState<InputValues>('');
	const [email, set_email] = useState<InputValues>('');
	const [phone, set_phone] = useState<InputValues>('');
	const [tags, set_tags] = useState<string[]>([]);
	const [contractStart, set_contractStart] = useState<InputValues>('');
	const [contractEnd, set_contractEnd] = useState<InputValues>('');
	const [error, set_error] = useState<{[key: string]: string}>({});

	const [documents, set_documents] = useState<File[]>([]);

	const [runDevicesCheck, set_runDevicesCheck] = useState(0);

	const [load, set_load] = useState(false);

	const navigate = useNavigate();
	const params = useParams();
	const TEXTE = LANGUAGE(lang);

	useEffect(() => set_view(params.view || 'home'), [params.view]);

	useEffect(() => {
		if (modal)
			return;

		API.getUsersTags(user)
		.then(response => set_tagsList(response?.data || []));
	}, [modal]);

	function saveUser(selectedDevices: string[]) {
		set_load(true);

		const apiData: PostUserData = {
			job,
			tags,
			email,
			phone,
			lastname,
			firstname,
			noHubspot:     true,
			addMethod:     'manually',
			noAssign:      selectedDevices.length === 0,
			contractEnd:   contractEnd ? moment(contractEnd, 'DD/MM/YYYY').toDate() : undefined,
			contractStart: contractStart ? moment(contractStart, 'DD/MM/YYYY').toDate() : undefined
		};

		// --- Post User
		dispatch(postCompanyUsersApi({data: apiData, user}))
		.then(async (response) => {
			if (!response?.payload?._id) {
				if (response.payload?.error === 'email already exist') {
					set_error({email: TEXTE.users.emailAlreadyExist});
					set_step(1);
				}

				return set_load(false);
			}

			let notificationData = ft_genNotification(TEXTE, 'addUser', img_faceSmile, ft_formatUserName(response.payload));
			notificationId = ft_pushNotification(dispatch, notificationId, notificationData);

			// --- Assign Selected Devices
			if (selectedDevices.length > 0) {
				await dispatch(assignDevicesApi({
					user,
					devicesId:  selectedDevices,
					assignUser: response.payload
				}));

				notificationData = ft_genNotification(TEXTE, 'assignDeviceSuccess', img_device, ft_formatUserName(response.payload));
				notificationId = ft_pushNotification(dispatch, notificationId, notificationData);
			}

			// --- Upload Profile Picture
			if (profilePicture) {
				dispatch(uploadProfilePictureApi({
					user,
					file:   profilePicture,
					idUser: response.payload._id
				}));
			}

			// --- Upload Documents
			documents.map((document, i) => {
				if (document.size > 2097152)
					return;

				const index = documentsUpload.length + i;
				const dispatchData = {index, dispatch, function: setUploadPercent};

				const uploadFileData = {
					percent:  0,
					filename: document.name,
					size:     document.size,
					id:       response.payload._id
				};

				dispatch(pushUpload(uploadFileData));

				API.uploadUsersDocument(response.payload._id, document, user, dispatchData)
				.then((responseUpload) => {
					if (!responseUpload || !responseUpload.status)
						return dispatch(setUploadPercent({index, percent: `error: ${responseUpload?.error || ''}`}));

					notificationData = ft_genNotification(TEXTE, 'fileDownloadDone', img_upload, ft_formatUserName(response.payload));
					notificationId = ft_pushNotification(dispatch, notificationId, notificationData);
				});
			});

			set_load(false);
			navigate(`/users/${response.payload._id}`);
		});
	}

	function checkStepIsValid() {
		const newError: {[key: string]: string} = {};

		if (step === 1) {
			if (!firstname)
				newError.firstname = 'required';
			if (!lastname)
				newError.lastname = 'required';
			if (!email)
				newError.email = 'required';
			else if (!regex_email.test(email.toString()))
				newError.email = TEXTE.users.emailNotValid;
			if (phone && !regex_phone.test(phone.toString()))
				newError.phone = TEXTE.users.phoneNotValid;
		}

		set_error(newError);

		if (Object.keys(newError).length === 0 && step < 3)
			return set_step(step + 1);
		if (step === 3)
			set_runDevicesCheck(e => e + 1);
	}

	function renderStep1() {
		return (
			<div>
				<MultiInputContainer>
					<Input
						required
						value={firstname}
						error={error.firstname}
						onChange={set_firstname}
						title={TEXTE.global.firstname}
						placeholder={TEXTE.placeholder.firstname}
					/>
					<Input
						required
						value={lastname}
						error={error.lastname}
						onChange={set_lastname}
						title={TEXTE.global.lastname}
						placeholder={TEXTE.placeholder.lastname}
					/>
				</MultiInputContainer>

				<MultiInputContainer>
					<Input
						required
						value={email}
						error={error.email}
						onChange={set_email}
						title={TEXTE.global.email}
						placeholder={TEXTE.placeholder.email}
					/>
					<Input
						value={phone}
						error={error.phone}
						onChange={set_phone}
						title={TEXTE.global.phone}
						placeholder={TEXTE.placeholder.phone}
					/>
				</MultiInputContainer>

				<MultiInputContainer>
					<Input
						type={'date'}
						value={contractStart}
						onChange={set_contractStart}
						title={TEXTE.global.contractStart}
						placeholder={TEXTE.placeholder.date}
					/>
					<Input
						type={'date'}
						value={contractEnd}
						onChange={set_contractEnd}
						title={TEXTE.global.contractEnd}
						placeholder={TEXTE.placeholder.date}
					/>
				</MultiInputContainer>

				<Input
					value={job}
					onChange={set_job}
					title={TEXTE.global.job}
					placeholder={TEXTE.placeholder.job}
				/>

				<div className={'separator'} />

				<Select
					hideChevron
					title={TEXTE.users.addTags}
					onClick={() => set_modal('tags')}
					classNameValue={'addUserTagsSelect'}
					placeholder={tags.length > 0 && tags.map((e, i) => <Badge key={`badge-${i}`} text={e} color={'tags'} />)}
				/>

				<ProfilePicture
					profilePictureFile={profilePicture}
					profilePictureImg={profilePictureImg}
					setProfilePictureFile={set_profilePicture}
					setProfilePictureImg={set_profilePictureImg}
				/>
			</div>
		);
	}

	function renderStep2() {
		return (
			<div className={'addUserStep step2'}>
				<UsersDocuments
					documents={documents}
					setDocument={set_documents}
				/>
			</div>
		);
	}

	function renderStep3() {
		return (
			<div className={'addUserStep step3'}>
				<UsersModalAssignDevice
					runCheck={runDevicesCheck}
					onSave={saveUser}
				/>
			</div>
		);
	}

	function renderButtonContainer() {
		return (
			<ButtonStickyContainer>
				<Button
					fullWidth
					disabled={load}
					type={'tertiary-gray'}
					onClick={() => (step === 1 ? navigate('/users/onboarding') : set_step(step - 1))}
				>
					{step === 1 ? TEXTE.global.cancel : TEXTE.global.previous}
				</Button>

				<Button
					fullWidth
					isLoad={load}
					onClick={checkStepIsValid}
				>
					{step === 3 ? TEXTE.global.validAndSave : TEXTE.global.next}
				</Button>
			</ButtonStickyContainer>
		);
	}

	if (view === 'import')
		return <UsersImport />;

	return (
		<div className={'globalDataContainer'}>
			<div className={'globalAddHeader'}>
				<Icon
					icon={img_userPlus}
					iconColor={'#000000'}
				/>
				<div className={'globalAddHeaderText'}>
					<span>{TEXTE.users.addUser}</span>
					<span>{TEXTE.users.createIn2Minute}</span>
				</div>
			</div>

			<div className={'globalAddContainer'}>
				<Tabs
					step
					actual={step}
					onChange={val => set_step(val as number)}
					data={[
						{value: 1, render: TEXTE.users.aboutUser},
						{value: 2, render: `${TEXTE.users.userDocuments} (${TEXTE.global.optional})`},
						{value: 3, render: `${TEXTE.users.setDevice} (${TEXTE.global.optional})`}
					]}
				/>

				{step === 1 && renderStep1()}
				{step === 2 && renderStep2()}
				{step === 3 && renderStep3()}
			</div>
			{renderButtonContainer()}

			{modal === 'tags' &&
				<ModalTags
					load={load}
					tags={tags}
					allTags={tagsList}
					onClose={() => set_modal(null)}
					onConfirm={(newTags) => {
						set_tags(newTags);
						set_modal(null);
					}}
				/>
			}
		</div>
	);
}

export default UserAdd;
